"use client";

import { useEffect, useState, type ReactNode } from "react";
import { Check, ShoppingBag } from "lucide-react";
import { useTranslations } from "next-intl";

import { cn } from "@/lib/utils";
import { useAtcStatus } from "./add-to-cart-form";

interface StickyBarProps {
	productName: string;
	selectionSummary?: string | null;
	priceContent: ReactNode;
	priceContentByQuantity?: Record<number, ReactNode>;
	expressBadge?: ReactNode;
	show: boolean;
}

export function StickyBar({
	productName,
	selectionSummary,
	priceContent,
	priceContentByQuantity,
	expressBadge,
	show,
}: StickyBarProps) {
	// Visible whenever the inline ATC button is NOT on screen. The inline button
	// sits below the fold, so the bar shows immediately at first paint and hides
	// only while the buyer can actually reach the inline button. (Replaces a
	// 400px scroll threshold, which is brittle on tall hero/gallery layouts.)
	const [visible, setVisible] = useState(true);
	const tBuyBox = useTranslations("pdpUx.buyBox");
	const tProduct = useTranslations("product");
	const atcStatus = useAtcStatus();

	useEffect(() => {
		const form = document.getElementById("pdp-atc-form");
		if (!form) return;

		// Watch the inline submit button, not the whole form. The form starts with
		// the dose and pack pickers and is roughly 480 px tall on a phone, so
		// observing it hid the bar as soon as its top edge scrolled in, about 370 px
		// before the inline button was reachable, and on tall viewports (form top
		// above the fold, button below it) removed the only CTA from the first
		// screen. The bar's own submit button lives inside the same form and is
		// excluded by its surface attribute.
		const target =
			form.querySelector<HTMLElement>('button[type="submit"][data-add-surface="pdp_buybox"]') ?? form;

		const observer = new IntersectionObserver(
			([entry]) => {
				if (!entry) return;
				// `isIntersecting` flips on the first visible pixel; the bar should only
				// leave once at least half of the inline button is really on screen.
				setVisible(!(entry.isIntersecting && entry.intersectionRatio >= 0.5));
			},
			{ threshold: [0, 0.5] },
		);
		observer.observe(target);
		return () => observer.disconnect();
	}, []);

	if (!show || !visible) return null;
	const pending = atcStatus?.pending ?? false;
	const showSuccess = atcStatus?.showSuccess ?? false;
	const quantity = atcStatus?.quantity ?? 1;
	const buttonLabel = showSuccess
		? tProduct("addedToBag")
		: pending
			? tProduct("adding")
			: tBuyBox("addToCart");
	const displayedPrice = priceContentByQuantity?.[quantity] ?? priceContent;

	return (
		<div
			className={cn(
				"fixed bottom-0 left-0 right-0 z-30 border-t border-border bg-card px-4 pt-3 shadow-[0_-4px_20px_rgba(0,0,0,0.06)] md:hidden",
				"pb-[max(0.75rem,env(safe-area-inset-bottom))]",
			)}
		>
			<div className="mb-2 flex items-center justify-between gap-3">
				<div className="min-w-0 flex-1">
					<p className="truncate text-[11px] text-muted-foreground">{productName}</p>
					{selectionSummary ? (
						<p className="truncate text-xs font-semibold text-foreground">
							{selectionSummary}
							{quantity > 1 ? ` · × ${quantity}` : ""}
						</p>
					) : null}
					<div className="text-base font-extrabold text-primary">{displayedPrice}</div>
				</div>
				{expressBadge}
			</div>
			<button
				type="submit"
				data-add-surface="pdp_sticky"
				form="pdp-atc-form"
				disabled={pending}
				className={cn(
					"hover:bg-primary/90 inline-flex w-full items-center justify-center gap-2 rounded-md bg-primary px-4 py-3.5 text-sm font-semibold text-primary-foreground transition-colors disabled:cursor-wait disabled:opacity-80",
					showSuccess && "bg-success hover:bg-success",
				)}
			>
				<span className="t-icon-swap" data-state={showSuccess ? "b" : "a"} aria-hidden="true">
					<span className="t-icon" data-icon="a">
						<ShoppingBag className="h-3.5 w-3.5" strokeWidth={2} />
					</span>
					<span className="t-icon" data-icon="b">
						<Check className="h-3.5 w-3.5" strokeWidth={2} />
					</span>
				</span>
				{buttonLabel}
			</button>
		</div>
	);
}
